import { useMemo } from "react";

import { PageTreeNodeData } from "@/patterns/agile-overview-sidenav/src";
import {
    delocalizePathname,
    isEqualUnlocalizedPathname
} from "@/utils/localization";

import { useLocation } from "../../../utils/location-provider";

function isLocation(urlSlug: string, location: Location): boolean {
    const url = new URL(delocalizePathname(urlSlug), location.origin);
    return (
        isEqualUnlocalizedPathname(url.pathname, location.pathname) &&
        url.hash == location.hash
    );
}

export type Page = {
    name: string;
    urlSlug: string;
};

function findNextPage(
    nodes: PageTreeNodeData[],
    location: Location
): Page | null {
    let foundCurrent = false;
    const queue = [...nodes].reverse();
    while (queue.length) {
        const next = queue.pop();
        if (!next) continue;

        if (next.urlSlug && isLocation(next.urlSlug, location)) {
            foundCurrent = true;
        } else if (foundCurrent && next.urlSlug != null) {
            return { name: next.name, urlSlug: next.urlSlug };
        }

        if (next?.children != null) {
            for (let i = next.children.length - 1; i >= 0; i--) {
                const child = next.children[i];
                if (child) queue.push(child);
            }
        }
    }
    return null;
}

export function useNextPage(root: Page, nodes: PageTreeNodeData[]): Page {
    const location = useLocation();
    return useMemo(() => {
        // Base case: current location is agile hub landing page,
        // so find the first node with a `urlSlug`.
        if (isLocation(root.urlSlug, location)) {
            for (const node of nodes) {
                if (node.urlSlug != null) {
                    return { name: node.name, urlSlug: node.urlSlug };
                }
            }
        }
        // Otherwise, find the next page.
        // If the current location is not found, or there is
        // no page after the current location, return the root.
        return findNextPage(nodes, location) ?? root;
    }, [root, nodes, location]);
}
